import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
	AdminClient,
	type ApiKeySummary,
	type IssuedApiKey,
	type McpServerSummary,
	type ToolCatalog
} from "../api/admin-client.ts"
import { AddToClaudeSection } from "../components/AddToClaudeSection.tsx"
import type { AuthContext } from "../components/AdminAuthGate.tsx"
import { CopyRow } from "../components/CopyRow.tsx"
import { IssueKeyWizard } from "../components/IssueKeyWizard.tsx"
import { ToolMatrix } from "../components/ToolMatrix.tsx"
import { McpPlayground } from "./McpPlayground.tsx"

type TabKey = "tools" | "keys" | "playground"

const TABS: ReadonlyArray<{ readonly key: TabKey; readonly label: string }> = [
	{ key: "tools", label: "도구" },
	{ key: "keys", label: "키" },
	{ key: "playground", label: "플레이그라운드" }
]

export function ServerDetailPage(props: { readonly auth: AuthContext }) {
	const { slug, name } = useParams<{ slug: string; name: string }>()
	const [server, setServer] = useState<McpServerSummary | undefined>()
	const [catalog, setCatalog] = useState<ToolCatalog | undefined>()
	const [error, setError] = useState<string | undefined>()
	const [tab, setTab] = useState<TabKey>("tools")
	const [lastIssuedPlainKey, setLastIssuedPlainKey] = useState<string | undefined>()
	const [keyCount, setKeyCount] = useState<number | undefined>()

	const client = useMemo(
		() => new AdminClient(props.auth.baseUrl, props.auth.invalidateSession),
		[props.auth.baseUrl, props.auth.invalidateSession]
	)

	const refreshServer = useCallback(async () => {
		if (!slug || !name) return
		try {
			setServer(await client.getMcpServer(slug, name))
		} catch (e) {
			setError((e as Error).message)
		}
	}, [slug, name, client])

	useEffect(() => {
		if (!slug || !name) return
		let cancelled = false
		void (async () => {
			try {
				const [s, cat] = await Promise.all([client.getMcpServer(slug, name), client.getToolCatalog()])
				if (!cancelled) {
					setServer(s)
					setCatalog(cat)
				}
			} catch (e) {
				if (!cancelled) setError((e as Error).message)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [slug, name, client])

	if (!slug || !name)
		return (
			<div className="content">
				<div className="alert alert-danger">slug 또는 server name 없음</div>
			</div>
		)
	if (error)
		return (
			<div className="content">
				<div className="alert alert-danger">{error}</div>
			</div>
		)
	if (!server)
		return (
			<div className="content">
				<p className="muted">불러오는 중...</p>
			</div>
		)

	const endpoint = `${props.auth.baseUrl}/mcp/${slug}/${name}`

	return (
		<div className="content">
			<div className="breadcrumb">
				<Link to="/services">서비스</Link> / <Link to={`/services/${slug}`}>{slug}</Link> / 서버 /{" "}
				<span>{name}</span>
			</div>

			<div className="page-head">
				<div>
					<h2>{server.displayName}</h2>
					<div className="sub">
						<code className="inline">{server.name}</code>
						{server.description && <span className="muted"> · {server.description}</span>}
					</div>
				</div>
			</div>

			<div className="panel">
				<h3>엔드포인트</h3>
				<CopyRow value={endpoint} />
			</div>

			<TabBar
				active={tab}
				onChange={setTab}
				counts={{
					tools: server.enabledTools.length,
					keys: keyCount
				}}
			/>

			{tab === "tools" && (
				<ToolsTab
					client={client}
					slug={slug}
					name={name}
					server={server}
					catalog={catalog}
					refresh={refreshServer}
				/>
			)}
			{tab === "keys" && (
				<KeysTab
					client={client}
					slug={slug}
					name={name}
					server={server}
					catalog={catalog}
					onNewPlainKey={setLastIssuedPlainKey}
					onCount={setKeyCount}
				/>
			)}
			{tab === "playground" && (
				<McpPlayground
					auth={props.auth}
					serviceSlug={slug}
					serverName={name}
					apiKey={lastIssuedPlainKey}
					enabledTools={server.enabledTools}
				/>
			)}
		</div>
	)
}

function TabBar(props: {
	readonly active: TabKey
	readonly onChange: (next: TabKey) => void
	readonly counts?: Readonly<Partial<Record<TabKey, number | undefined>>>
}) {
	return (
		<div className="tab-bar">
			{TABS.map((t) => {
				const isActive = props.active === t.key
				const count = props.counts?.[t.key]
				return (
					<button
						key={t.key}
						type="button"
						onClick={() => props.onChange(t.key)}
						className={`tab-btn${isActive ? " is-active" : ""}`}
					>
						{t.label}
						{count !== undefined && <span className="tab-count">{count}</span>}
					</button>
				)
			})}
		</div>
	)
}

function ToolsTab(props: {
	readonly client: AdminClient
	readonly slug: string
	readonly name: string
	readonly server: McpServerSummary
	readonly catalog: ToolCatalog | undefined
	readonly refresh: () => Promise<void>
}) {
	const [selected, setSelected] = useState<ReadonlyArray<string>>(() => [...props.server.enabledTools])
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | undefined>()
	const [saved, setSaved] = useState(false)

	// 외부에서 server 가 갱신되면 선택 상태도 초기화한다.
	useEffect(() => {
		setSelected([...props.server.enabledTools])
		setSaved(false)
	}, [props.server])

	const dirty = useMemo(() => {
		const a = [...props.server.enabledTools].sort()
		const b = [...selected].sort()
		if (a.length !== b.length) return true
		return a.some((v, i) => v !== b[i])
	}, [props.server.enabledTools, selected])

	async function save() {
		if (selected.length === 0) {
			setError("enabled tool 이 최소 1개 이상이어야 합니다")
			return
		}
		setBusy(true)
		setError(undefined)
		setSaved(false)
		try {
			await props.client.updateMcpServerTools(props.slug, props.name, selected)
			await props.refresh()
			setSaved(true)
		} catch (e) {
			setError((e as Error).message)
		} finally {
			setBusy(false)
		}
	}

	return (
		<div className="panel">
			<div className="panel-head">
				<h3>활성 도구</h3>
				<button type="button" className="btn btn-primary btn-sm" disabled={!dirty || busy} onClick={save}>
					{busy ? "저장 중..." : "적용"}
				</button>
			</div>

			{error && (
				<div className="alert alert-danger" style={{ marginBottom: 14 }}>
					{error}
				</div>
			)}
			{saved && !dirty && (
				<div className="alert alert-info" style={{ marginBottom: 14 }}>
					저장되었습니다.
				</div>
			)}

			<ToolMatrix catalog={props.catalog} value={selected} onChange={setSelected} />

			<PublicDocsMcpCard />
		</div>
	)
}

/**
 * 공개 docs MCP 엔드포인트 안내 카드.
 *
 * `/mcp/docs` 는 API 키 없이 접근 가능한 플랫폼 공통 docs 전용 엔드포인트(ADR: docs 는 serviceSlug
 * 독립). 서비스별 MCP 서버에 `docs_*` 을 enable 해야 할 동기를 줄여주고, 외부 에이전트/문서 탐색
 * 용도를 키 발급 플로우에서 해방시킨다.
 */
function PublicDocsMcpCard() {
	const url = typeof window === "undefined" ? "/mcp/docs" : `${window.location.origin}/mcp/docs`
	return (
		<div
			style={{
				marginTop: "var(--pk-space-5)",
				padding: "var(--pk-space-4)",
				background: "var(--pk-bg-sunken)",
				border: "1px solid var(--pk-border-subtle)",
				borderRadius: "var(--pk-radius-md)"
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 8,
					marginBottom: 6
				}}
			>
				<span style={{ fontSize: 14, fontWeight: 600 }}>📖 공개 Docs MCP</span>
				<span
					style={{
						fontSize: 10,
						padding: "1px 6px",
						borderRadius: "var(--pk-radius-pill)",
						background: "var(--pk-success-soft)",
						color: "var(--pk-success)",
						textTransform: "uppercase",
						letterSpacing: "0.08em"
					}}
				>
					No API Key
				</span>
			</div>
			<p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--pk-text-muted)", lineHeight: 1.5 }}>
				Docs 툴(<code className="inline">docs_list</code> · <code className="inline">docs_search</code> ·{" "}
				<code className="inline">docs_read</code>)은 서비스 독립 플랫폼 공통 지식이라 전역 공개 엔드포인트에서
				바로 쓸 수 있습니다. 이 서버의 Enabled Tools 에서는 체크를 꺼두어도 외부 에이전트가 아래 URL 로 접근
				가능합니다.
			</p>
			<code
				className="inline"
				style={{ fontSize: 12, padding: "4px 8px", display: "inline-block", wordBreak: "break-all" }}
			>
				{url}
			</code>
		</div>
	)
}

function KeysTab(props: {
	readonly client: AdminClient
	readonly slug: string
	readonly name: string
	readonly server: McpServerSummary
	readonly catalog: ToolCatalog | undefined
	readonly onNewPlainKey: (plain: string | undefined) => void
	readonly onCount?: (n: number) => void
}) {
	const [keys, setKeys] = useState<ReadonlyArray<ApiKeySummary>>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | undefined>()
	const [wizardOpen, setWizardOpen] = useState(false)
	/** 발급 직후 키 — AddToClaudeSection 에 전달. 페이지 이동/재로드 시 소멸 */
	const [lastIssued, setLastIssued] = useState<IssuedApiKey | undefined>()

	const refresh = useCallback(async () => {
		try {
			const list = await props.client.listApiKeys(props.slug, props.name)
			setKeys(list)
			props.onCount?.(list.filter((k) => !k.revokedAt).length)
		} catch (e) {
			setError((e as Error).message)
		} finally {
			setLoading(false)
		}
	}, [props.client, props.slug, props.name, props.onCount])

	useEffect(() => {
		let cancelled = false
		setLoading(true)
		props.client
			.listApiKeys(props.slug, props.name)
			.then((list) => {
				if (!cancelled) {
					setKeys(list)
					props.onCount?.(list.filter((k) => !k.revokedAt).length)
				}
			})
			.catch((e: Error) => {
				if (!cancelled) setError(e.message)
			})
			.finally(() => {
				if (!cancelled) setLoading(false)
			})
		return () => {
			cancelled = true
		}
	}, [props.client, props.slug, props.name, props.onCount])

	async function revoke(keyId: string) {
		if (!window.confirm(`정말 이 키(${keyId})를 취소하시겠습니까?`)) return
		try {
			await props.client.revokeApiKey(props.slug, props.name, keyId)
			await refresh()
		} catch (e) {
			setError((e as Error).message)
		}
	}

	async function handleIssue(input: Parameters<AdminClient["issueApiKey"]>[0]): Promise<IssuedApiKey> {
		const result = await props.client.issueApiKey(input)
		props.onNewPlainKey(result.plainKey)
		setLastIssued(result)
		await refresh()
		return result
	}

	return (
		<>
			{/* 발급 직후 "Add to Claude" 섹션 — plainKey 가 있는 동안만 노출 */}
			{lastIssued && (
				<div className="panel" style={{ marginBottom: "var(--pk-space-4)" }}>
					<div className="panel-head" style={{ marginBottom: "var(--pk-space-4)" }}>
						<h3>발급 완료</h3>
						<button type="button" className="btn btn-ghost btn-sm" onClick={() => setLastIssued(undefined)}>
							닫기
						</button>
					</div>
					<div className="alert alert-warn" style={{ marginBottom: "var(--pk-space-4)" }}>
						아래 키는 이번 한 번만 표시됩니다. 안전한 곳에 저장하거나 아래에서 바로 복사하세요.
					</div>
					<AddToClaudeSection
						serviceSlug={props.slug}
						serverName={props.name}
						apiKey={lastIssued.plainKey}
						keyLabel={lastIssued.record.label || lastIssued.record.keyId}
					/>
				</div>
			)}

			<div className="panel">
				<div className="panel-head">
					<h3>API 키</h3>
					<button type="button" className="btn btn-primary btn-sm" onClick={() => setWizardOpen(true)}>
						새 키 발급
					</button>
				</div>

				{error && (
					<div className="alert alert-danger" style={{ marginBottom: 14 }}>
						{error}
					</div>
				)}

				{loading ? (
					<p className="muted">불러오는 중...</p>
				) : keys.length === 0 ? (
					<p className="muted" style={{ fontSize: 13 }}>
						발급된 키가 없습니다. "새 키 발급" 으로 시작하세요.
					</p>
				) : (
					<div
						style={{ overflow: "hidden", border: "1px solid var(--border)", borderRadius: "var(--radius)" }}
					>
						<table className="table">
							<thead>
								<tr>
									<th>라벨</th>
									<th>스코프</th>
									<th>생성</th>
									<th>마지막 사용</th>
									<th>만료</th>
									<th style={{ width: 90 }} />
								</tr>
							</thead>
							<tbody>
								{keys.map((key) => (
									<tr key={key.keyId}>
										<td>
											<div>{key.label || "—"}</div>
											<code className="inline" style={{ fontSize: 10 }}>
												{key.keyId}
											</code>
										</td>
										<td>
											<div className="chips">
												{key.scopes.map((s) => (
													<span key={s} className="chip">
														{s}
													</span>
												))}
											</div>
										</td>
										<td className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
											{new Date(key.createdAt).toLocaleDateString("ko-KR")}
										</td>
										<td className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
											{key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString("ko-KR") : "—"}
										</td>
										<td className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
											{key.expiresAt ? new Date(key.expiresAt).toLocaleDateString("ko-KR") : "무기한"}
										</td>
										<td>
											{key.revokedAt ? (
												<span className="badge badge-disabled">취소됨</span>
											) : (
												<button
													type="button"
													className="btn btn-danger btn-sm"
													onClick={() => revoke(key.keyId)}
												>
													취소
												</button>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{wizardOpen && (
				<IssueKeyWizard
					slug={props.slug}
					serverName={props.name}
					serverEnabledTools={props.server.enabledTools}
					catalog={props.catalog}
					onClose={() => setWizardOpen(false)}
					onIssue={handleIssue}
				/>
			)}
		</>
	)
}
